import { useState, useEffect, useContext, useMemo } from "react";
import AgGridLayout from "@wrappers/layouts/AgGridLayout";
import { ColDef, GridApi, ValueFormatterParams } from "ag-grid-community";
import { useDispatch, useSelector } from "react-redux";
import Layout from "@wrappers/layouts/main-layout/Layout";
import { customFilterOptions, mainContext } from "@ts/constants";
import {
    ChangeInValueCellRenderer,
    ProductAnchorCellRenderer,
    AdGroupAnchorCellRenderer,
    ProductPageLinkButtonRenderer,
    ProductActionCellRenderer,
    PopOverNameCellRenderer,
    TagsCellRenderer,
} from "@cell-renderers/AllCellRenderers";
import { useClearGridData, useGetModulesData, usePermissionCheck } from "@hooks/AllCustomHooks";
import { createDatasource } from "@ts/retrieve-data";
import { updateTitle } from "@features/titleSlice";
import { updateActiveModule } from "@features/activeModuleSlice";
import {
    onProductSelectionChanged,
    numericValueFormatter,
    addToBuybox,
    getFocusedAsinsList,
    getClientTagsList
} from "@ts/utils";
import { getDateObj } from "@ts/date-range";
import { setIsLoader } from "@features/loaderSlice";
import { CreateCampaignModal } from "@modals/campaign-modal/CreateCampaignModal";
import {
    updateCreateCampaignModal,
    updateShowRequestAlert,
} from "@features/modalSlice";
import AddRulesModal from "@modals/rules-modal/AddRulesModal";
import * as Sentry from "@sentry/react";
import {
    IAdminToggleBtn,
    IChannel,
    IMainContext,
    IValueBoolean,
    ParentModule,
    ProductAd,
    TActiveClientId,
    TagColFields,
    TBuyboxEnabledDataRedux,
    TClientTags
} from "@ts/common-types";
import RequestAlertModal from "@modals/RequestAlertModal";
import { Link } from "react-router-dom";
import { setProductAnalyticsData, updateRecordTagsData } from "@/features/gridDataSlice";
import { setFocusedAsins } from "@/features/adminSettingsSlice";
import { updateClientTagsList } from "@/features/clientDataSlice";
import { filter, flatten, pluck, uniq } from "underscore";
import PADeepDiveButtonRenderer from "@/components/cell-renderers/PADeepDiveButtonRenderer";
import CustomTagTypeFilter from "@/components/filters/CustomTagTypeFilter";
import ProductAnalyticsDeepDiveModal from "@/components/modals/ProductAnalyticsDeepDiveModal";
import CustomCampaignTagFilter from "@/components/filters/CustomCampaignTagFilter";

type TCreateCampaignModalDisp = {
    createCampaignModalDisp: IValueBoolean;
};

type TIsBuyboxUpdated = {
    isBuyboxUpdated: IValueBoolean;
};

type TDoPreferredSellersExist = {
    doPreferredSellersExist: IValueBoolean;
};

type TShowRequestAlert = {
    showRequestAlert: IValueBoolean;
};

const ProductAnalytics = () => {
    const clearGridData = useClearGridData();
    const dispatch = useDispatch();
    const {
        dates,
        DateRange,
        cerbosPermissions
    } = useContext<IMainContext>(mainContext);
    const [gridApi, setGridApi] = useState<GridApi>();
    const [loading, setLoading] = useState<boolean>(true);
    const channel = useSelector((state: IChannel) => state.channel.value);
    const productAnalyticsData = useSelector((state: any) => state.productAnalyticsData.value);
    const createCampaignModalDisp = useSelector(
        (state: TCreateCampaignModalDisp) => state.createCampaignModalDisp.value
    );
    const enableBuybox = useSelector(
        (state: IAdminToggleBtn) => state.enableBuybox.value
    );
    const isBuyboxUpdated = useSelector(
        (state: TIsBuyboxUpdated) => state.isBuyboxUpdated.value
    );
    const showRequestAlert = useSelector(
        (state: TShowRequestAlert) => state.showRequestAlert.value
    );
    const buyboxEnabledData = useSelector(
        (state: TBuyboxEnabledDataRedux) => state.buyboxEnabledData.value
    );
    const doPreferredSellersExist = useSelector(
        (state: TDoPreferredSellersExist) => state.doPreferredSellersExist.value
    );
    const activeClientId = useSelector(
        (state: TActiveClientId) => state.activeClientId.value
    );
    const updateRequestHandler = (overwriteRequest: boolean) => {
        addToBuybox(overwriteRequest, buyboxEnabledData);
        dispatch(updateShowRequestAlert(!showRequestAlert));
    };

    const gridParams = useGetModulesData({ type: "grid-params" });
    const deepDiveModalDisplay = useSelector((state: any) => state.deepDiveModalDisplay.value);

    const populateProductAnalytics = async () => {
        return await createDatasource("product-analytics", {
            dateObj: getDateObj(),
        });
    };

    useEffect(() => {
        getClientTagsList()
            .then((tagsList: TClientTags[]) => {
                dispatch(
                    updateClientTagsList(
                        filter(
                            tagsList,
                            (tag) => ["global", "product"].includes(tag.record_type)
                        )
                    )
                );
            })
            .catch((error) => {
                console.error(error);
            });
    }, [activeClientId, channel]);

    useEffect(() => {
        clearGridData();
        dispatch(updateTitle("Product Analytics"));
        dispatch(updateActiveModule("product analytics"));
        dispatch(setIsLoader(true));
        setLoading(true);
        getFocusedAsinsList()
            .then((focusedAsins) => {
                dispatch(setFocusedAsins(focusedAsins));
            })
            .catch((err) => {
                console.error(err);
                Sentry.captureException(err);
            });

        populateProductAnalytics()
            .then((products) => {
                dispatch(setProductAnalyticsData(products as Array<ProductAd>));
                dispatch(updateRecordTagsData(uniq(flatten(pluck(products, "sku_tags")))));
                setLoading(false);
                dispatch(setIsLoader(false));
            })
            .catch((err) => {
                console.error(err);
                Sentry.captureException(err);
            });
        // eslint-disable-next-line
    }, [activeClientId, dates, DateRange, dispatch, isBuyboxUpdated, channel]);

    const { canCreateRule } = usePermissionCheck();

    const initialProductsColumnDefs = useGetModulesData({
        type: "columns",
        colDefs: [
            {
                field: "sku_title",
                headerName: "Product",
                minWidth: 350,
                maxWidth: 450,
                filter: "agTextColumnFilter",
                pinned: "left",
                checkboxSelection: true,
                headerCheckboxSelection: true,
                lockPinned: true,
                cellClass: "lock-pinned",
                tooltipField: "sku_title",
                cellRenderer: ProductAnchorCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    pinnedColumnValue: "Total",
                },
            },
            {
                field: "asin",
                headerName: "SKU ID",
                filter: "agTextColumnFilter",
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                hide: gridParams.columnsToHide?.includes("asin"),
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "product_link",
                headerName: "Product page",
                filter: false,
                suppressMenu: true,
                sortable: false,
                maxWidth: 90,
                cellStyle: { textAlign: "center" },
                cellRenderer: ProductPageLinkButtonRenderer,
                pinned: "left",
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "deep_dive",
                headerName: "Deep Dive",
                filter: false,
                suppressMenu: true,
                sortable: false,
                maxWidth: 90,
                cellStyle: { textAlign: "center" },
                cellRenderer: PADeepDiveButtonRenderer,
                pinned: "left",
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "category",
                headerName: "Category",
                minWidth: 200,
                filter: "agTextColumnFilter",
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                tooltipField: "category",
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "isFocusedAsin",
                headerName: "Actions",
                maxWidth: 180,
                minWidth: 100,
                filter: true,
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                cellStyle: { textAlign: "center" },
                cellRenderer: ProductActionCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "is_available",
                headerName: "Is AVL?",
                maxWidth: 180,
                minWidth: 100,
                pinned: "left",
                hide: gridParams.columnsToHide?.includes("is_available"),
                headerClass: "text-center",
                lockPinned: true,
                cellClass: "lock-pinned",
                filter: "agSetColumnFilter",
                valueFormatter: (params: ValueFormatterParams) =>
                    params.node.rowPinned
                        ? ""
                        : params.value !== null
                            ? params.value
                                ? "AVL"
                                : "UNAVL"
                            : "N/A",
                cellStyle: (params: ValueFormatterParams) => {
                    const style: Record<string, string> = { textAlign: "center" };
                    if (params.value === "AVL") {
                        style.color = "green";
                    } else if (params.value === "UNAVL") {
                        style.color = "red";
                    }
                    return style;
                },
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "ag_group_nos",
                headerName: "# Ad Groups",
                filter: false,
                cellClass: "lock-pinned",
                valueFormatter: (params: ValueFormatterParams) => params.data.adgroup_count || 0,
                cellRenderer: AdGroupAnchorCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "bsr",
                headerName: "BSR",
                minWidth: 140,
                filter: "agNumberColumnFilter",
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: false,
                    showChangePercent: false,
                    decimalPlace: 0,
                },
            },
            {
                headerName: gridParams["click"]?.headerName || "Clicks",
                field: "click",
                filter: "agNumberColumnFilter",
                minWidth: 160,
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                hide: gridParams.columnsToHide?.includes("click"),
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    decimalPlace: 0,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Spends",
                field: "spend",
                filter: "agNumberColumnFilter",
                minWidth: 160,
                sort: "desc",
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Impressions",
                field: "impression",
                filter: "agNumberColumnFilter",
                minWidth: 160,
                hide: gridParams.columnsToHide?.includes("impression"),
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    decimalPlace: 0,
                    showChangePercent: true,
                },
            },
            {
                headerName: "AOV",
                cellStyle: { whiteSpace: "normal" },
                field: "aov",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                    decimalPlace: 1,
                    hideInfinite: true
                },
            },
            {
                headerName: "Orders",
                field: "orders",
                filter: "agNumberColumnFilter",
                minWidth: 160,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    decimalPlace: 0,
                    showChangePercent: true,
                },
                hide: gridParams.columnsToHide?.includes("orders"),
            },
            {
                headerName: "Indirect Orders",
                cellStyle: { whiteSpace: "normal" },
                field: "total_view_ad_units",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                hide: true,
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Total Orders",
                cellStyle: { whiteSpace: "normal" },
                field: "total_clicked_ad_units",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                hide: true,
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Sales",
                field: "sale",
                filter: "agNumberColumnFilter",
                minWidth: 160,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Total Sales",
                field: "total_sale",
                filter: "agNumberColumnFilter",
                minWidth: 160,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CTR",
                field: "ctr",
                filter: "agNumberColumnFilter",
                minWidth: 140,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                    decimalPlace: 1,
                },
            },
            {
                headerName: gridParams["cpc"]?.headerName || "CPC",
                field: "cpc",
                filter: "agNumberColumnFilter",
                minWidth: 140,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                hide: gridParams.columnsToHide?.includes("cpc"),
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CPM",
                field: "cpm",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                minWidth: 140,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CR",
                field: "cr",
                filter: "agNumberColumnFilter",
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                minWidth: 140,
                hide: gridParams.columnsToHide?.includes("cr"),
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CPA",
                field: "cpa",
                filter: "agNumberColumnFilter",
                minWidth: 140,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                hide: gridParams.columnsToHide?.includes("cpa"),
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "ACoS",
                field: "acos",
                filter: "agNumberColumnFilter",
                minWidth: 140,
                hide: gridParams.columnsToHide?.includes("acos"),
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Total Ad Sales",
                cellStyle: { whiteSpace: "normal" },
                field: "total_clicked_ad_sales",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                field: "troas",
                headerName: "TRoAS",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 140,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "TACoS",
                field: "tacos",
                filter: "agNumberColumnFilter",
                minWidth: 140,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                },
            },
            {
                headerName: "RoAS",
                field: "roas",
                filter: "agNumberColumnFilter",
                minWidth: 140,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            // New SOV/MS percentage metrics (2-decimal precision)
            {
                headerName: "Overall SOV",
                field: "sov_overall_pct",
                filter: "agNumberColumnFilter",
                minWidth: 140,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                    decimalPlace: 2,
                },
            },
            {
                headerName: "Organic SOV",
                field: "sov_organic_pct",
                filter: "agNumberColumnFilter",
                minWidth: 140,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                    decimalPlace: 2,
                },
            },
            {
                headerName: "Paid SOV",
                field: "sov_paid_pct",
                filter: "agNumberColumnFilter",
                minWidth: 140,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                    decimalPlace: 2,
                },
            },
            {
                headerName: "MS (Price)",
                field: "ms_by_price_pct",
                filter: "agNumberColumnFilter",
                minWidth: 140,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                    decimalPlace: 2,
                },
            },
            {
                headerName: "MS (MRP)",
                field: "ms_by_mrp_pct",
                filter: "agNumberColumnFilter",
                minWidth: 140,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                    decimalPlace: 2,
                },
            },
            {
                field: "latest_bb_winner",
                headerName: "BB Winner",
                filter: "agTextColumnFilter",
                cellClass: "lock-pinned",
                tooltipField: "bb_winner",
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "rating",
                headerName: "Rating",
                maxWidth: 180,
                minWidth: 120,
                filter: "agNumberColumnFilter",
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                field: "rating_count",
                headerName: "# Ratings",
                maxWidth: 180,
                minWidth: 120,
                filter: "agNumberColumnFilter",
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            // {
            //     field: "price",
            //     headerName: "Price",
            //     minWidth: 140,
            //     filter: "agNumberColumnFilter",
            //     valueFormatter: numericValueFormatter,
            //     cellRenderer: ChangeInValueCellRenderer,
            //     cellRendererParams: {
            //         hasPinnedColumnValue: false,
            //         hasPercent: false,
            //         showChangePercent: true,
            //     },
            // },
            {
                field: "avg_price",
                headerName: "Avg. Price",
                minWidth: 140,
                filter: "agNumberColumnFilter",
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            // {
            //     field: "availability_percent",
            //     headerName: "AVL %",
            //     minWidth: 160,
            //     filter: "agNumberColumnFilter",
            //     cellRenderer: ChangeInValueCellRenderer,
            //     cellRendererParams: {
            //         hasPinnedColumnValue: false,
            //         hasPercent: true,
            //         showChangePercent: true,
            //         decimalPlace: 1,
            //     },
            // },
            {
                field: "weighted_availability",
                headerName: "Wt. Avl%",
                minWidth: 160,
                filter: "agNumberColumnFilter",
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: true,
                    showChangePercent: true,
                    decimalPlace: 2,
                },
            },
            {
                field: "tag_types",
                headerName: "Tag Types",
                filter: CustomTagTypeFilter,
                filterParams: {
                    tagMappingField: TagColFields.PRODUCT_TAGS
                },
                minWidth: 200,
                maxWidth: 250,
                cellRenderer: PopOverNameCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    tagMappingField: TagColFields.PRODUCT_TAGS
                },
                hide: true
            },
            {
                field: TagColFields.PRODUCT_TAGS,
                headerName: "Product Tags",
                filter: CustomCampaignTagFilter,
                minWidth: 200,
                maxWidth: 250,
                cellRenderer: TagsCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
        ]
    });

    const productsColumnDefs = useMemo(() =>
        !enableBuybox ?
            initialProductsColumnDefs.filter((data: ColDef) => data.headerName !== "Actions")
            :
            initialProductsColumnDefs
        , [initialProductsColumnDefs, enableBuybox]);

    return (
        <>
            <Layout>
                <div className="main-content-body subheader-fixed">
                    <div className="wrapper">
                        <div
                            id="products-analytics-grid"
                            className="ag-theme-alpine"
                            style={{
                                height: "calc(100vh - 150px)",
                                width: "100%",
                            }}
                        >
                            <AgGridLayout
                                setGridApi={setGridApi}
                                rowData={productAnalyticsData}
                                columnDefs={productsColumnDefs}
                                onSelectionChanged={onProductSelectionChanged}
                                loading={loading}
                                isTotalRowsReq={true}
                                rowHeight={gridParams?.rowHeight}
                            />
                        </div>
                    </div>
                </div>
                <CreateCampaignModal
                    show={createCampaignModalDisp}
                    setShow={(show) =>
                        dispatch(updateCreateCampaignModal(show))
                    }
                    gridApi={gridApi}
                />
                <AddRulesModal api={gridApi} canAddRule={canCreateRule} />
                {showRequestAlert && (
                    <RequestAlertModal
                        showRequestAlert={showRequestAlert}
                        bodyComponent={
                            <>
                                <div className="fs-5 text-center p-3">
                                    {doPreferredSellersExist
                                        ? `Are you sure you want to overwrite existing ASINs ?`
                                        : `Please add a seller to process your request`}
                                </div>
                            </>
                        }
                        footerComponent={
                            doPreferredSellersExist ? (
                                <>
                                    <div>
                                        <button
                                            className="btn btn-primary mx-1"
                                            onClick={() =>
                                                updateRequestHandler(true)
                                            }
                                        >
                                            No, Add Selected
                                        </button>
                                        <button
                                            className="btn btn-danger"
                                            onClick={() =>
                                                updateRequestHandler(false)
                                            }
                                        >
                                            Yes, Overwrite Existing
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div>
                                        <Link
                                            to="/settings"
                                            style={{
                                                cursor: "pointer",
                                                textDecoration: "none",
                                                color: "#000000",
                                            }}
                                            onClick={() =>
                                                dispatch(
                                                    updateShowRequestAlert(
                                                        !showRequestAlert
                                                    )
                                                )
                                            }
                                        >
                                            <span className="btn btn-primary mx-1">
                                                Proceed
                                            </span>
                                        </Link>

                                        <button
                                            className="btn btn-warning"
                                            onClick={() =>
                                                dispatch(
                                                    updateShowRequestAlert(
                                                        !showRequestAlert
                                                    )
                                                )
                                            }
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </>
                            )
                        }
                    />
                )}
                {deepDiveModalDisplay && <ProductAnalyticsDeepDiveModal show={deepDiveModalDisplay} />}
            </Layout>

        </>
    );
};

export default ProductAnalytics;
